/** @jsxImportSource @emotion/react */
import type { Editor } from '@tiptap/react';
import { useEffect, useState, useCallback } from 'react';

import FormatLineSpacingRoundedIcon from '@mui/icons-material/FormatLineSpacingRounded';
import PyMuiMenu from '../components/generalComponents/PyMuiMenu';
import PyMuiMenuItem from '../components/generalComponents/PyMuiMenuItem';
import { PyMuiIconButton } from '../components/generalComponents';
import { useActiveEditor } from '../ActiveEditorContext';
import { styled } from '@mui/material/styles';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectAllTextDefaultStyles } from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import { getNodeTypeFromSelection } from '../helpers/textStyleHelpers';

const DEFAULT_LINEHEIGHT = 'normal';

export const defaultLineHeightOptions = [
  { name: 'Default', level: DEFAULT_LINEHEIGHT },
  { name: 'Single', level: '1.2' },
  { name: '1.15', level: '1.38' },
  { name: '1.5', level: '1.799' },
  { name: 'Double', level: '2.4' },
];

const isActive = (editor: Editor | null, lineHeight: string) => editor?.isActive('lineHeight', { lineHeight }) ?? false;

const LineHeightContainer = styled('span')({
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'stretch',
});

const LineHeight = ({ disabled = false }: { disabled?: boolean }) => {
  const { activeEditor: editor } = useActiveEditor();
  const textDefaultStyles = useAppSelector(selectAllTextDefaultStyles);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<string>(DEFAULT_LINEHEIGHT);

  const getDefaultLineHeight = useCallback(() => {
    if (!editor) return DEFAULT_LINEHEIGHT;
    const nodeType = getNodeTypeFromSelection(editor);
    return textDefaultStyles[nodeType]?.lineHeight ?? DEFAULT_LINEHEIGHT;
  }, [editor, textDefaultStyles]);

  const handleOpenMenu = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelectLineHeight = useCallback(
    (lineHeight: string) => {
      editor?.chain().focus().setLineHeight(lineHeight).run();
      setSelected(lineHeight);
      handleClose();
    },
    [editor, handleClose]
  );

  const updateSelectedLineHeight = useCallback(() => {
    if (!editor?.isEditable) return;

    const activeType = editor.isActive('heading') ? 'heading' : 'paragraph';
    const lineHeight = editor.getAttributes(activeType)?.lineHeight;

    if (lineHeight) {
      setSelected(lineHeight);
      return;
    }

    const newDefaultLineHeight = getDefaultLineHeight();
    setSelected(newDefaultLineHeight);
  }, [editor, getDefaultLineHeight]);

  useEffect(() => {
    if (!editor) return;

    const handler = () => updateSelectedLineHeight();

    editor.on('selectionUpdate', handler);
    editor.on('transaction', handler);

    return () => {
      editor.off('selectionUpdate', handler);
      editor.off('transaction', handler);
    };
  }, [editor, updateSelectedLineHeight]);

  useEffect(() => {
    updateSelectedLineHeight();
  }, [textDefaultStyles, editor, updateSelectedLineHeight]);

  return (
    <LineHeightContainer>
      <PyMuiIconButton
        disabled={disabled}
        attributeName="lineheight"
        isMenuOpen={Boolean(anchorEl)}
        onClick={(e) => handleOpenMenu(e)}
        isActive={isActive(editor, selected)}
      >
        <FormatLineSpacingRoundedIcon />
      </PyMuiIconButton>

      <PyMuiMenu
        testId="lineheight-menu"
        id="select-lineheight-menu"
        menuListProps="select-lineheight-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleClose}
      >
        {defaultLineHeightOptions.map(({ name, level }) => (
          <PyMuiMenuItem
            key={name}
            testId={`lineheight-menu-item-${name.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => handleSelectLineHeight(level)}
            isActive={selected === level}
            menuItem={name}
          />
        ))}
      </PyMuiMenu>
    </LineHeightContainer>
  );
};

export default LineHeight;
